// @ts-check
// Fio de e-mail (doc 10 do mapeamento, 11.4): cada mensagem em um cartão com remetente, para, cc e data,
// corpo HTML em iframe isolado (sandbox, srcdoc, altura do conteúdo), anexos em chips, mensagens
// antigas recolhidas em uma linha e resposta com "Re:" no rodapé.
import { html } from 'htm/preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { Avatar } from '../../ui/avatar.js';
import { Icon } from '../../ui/icon.js';
import { Button, LightButton, LightIconButton } from '../../ui/button.js';
import { colorSchemeState } from '../../app/theme.js';
import {
  buildReplySubject,
  formatFullDateTime,
  formatMediumDateTime,
  formatThreadListTime,
  getMessageSnippet,
} from '../../lib/conversation-format.js';
import { DocumentChip, ImageAttachment } from './message-attachments.js';
import { MessageStatus } from './whatsapp-message-list.js';
import { PendingAttachmentList, usePendingAttachments } from './message-composer.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/conversations.js').ConversationMessage} ConversationMessage
 * @typedef {import('../../data/conversations.js').ConversationThread} ConversationThread
 * @typedef {import('../../data/conversations.js').MessageParticipant} MessageParticipant
 */

/** A partir deste total, as mensagens do meio ficam atrás do botão "N e-mails" (como no Twenty). */
const COLLAPSE_MIDDLE_THRESHOLD = 5;
const IFRAME_MIN_HEIGHT = 24;

/**
 * Cores do tema lidas do documento, para o corpo do e-mail acompanhar o claro e o escuro.
 */
function readThemeColors() {
  const style = getComputedStyle(document.documentElement);
  const read = (/** @type {string} */ name, /** @type {string} */ fallback) => style.getPropertyValue(name).trim() || fallback;
  return {
    text: read('--t-font-color-primary', '#333'),
    secondaryText: read('--t-font-color-tertiary', '#888'),
    link: read('--t-color-blue', '#1050d8'),
    border: read('--t-border-color-medium', '#ddd'),
    fontFamily: read('--t-font-family', 'Figtree, sans-serif'),
    isDark: document.documentElement.classList.contains('dark'),
  };
}

/**
 * Documento do iframe: estilo base, links em nova aba, sem scripts (o sandbox não permite).
 * @param {string} body
 */
function buildEmailDocument(body) {
  const colors = readThemeColors();
  // no tema escuro o e-mail vai numa folha clara (3.5.1): e-mails, inclusive os enviados pelo CRM com o layout da
  // escola, são desenhados com texto escuro sobre fundo claro e ficavam ilegíveis sobre o fundo escuro da conversa
  const paper = colors.isDark
    ? { background: '#f7f6f4', text: '#1f2937', secondaryText: '#6b7280', link: '#1050d8', border: '#d6d3d1', padding: '10px 12px', radius: '6px' }
    : { background: 'transparent', text: colors.text, secondaryText: colors.secondaryText, link: colors.link, border: colors.border, padding: '0', radius: '0' };
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="color-scheme" content="light"><base target="_blank">
<style>
:root { color-scheme: light; }
html { margin: 0; padding: 0; background: transparent; }
body { margin: 0; padding: ${paper.padding}; background: ${paper.background}; border-radius: ${paper.radius}; color: ${paper.text}; font-family: ${colors.fontFamily}; font-size: 13px; line-height: 1.5; overflow-wrap: anywhere; }
a { color: ${paper.link}; }
p { margin: 0 0 8px; }
p:last-child { margin-bottom: 0; }
ul, ol { margin: 0 0 8px; padding-left: 20px; }
img { max-width: 100%; height: auto; }
blockquote { margin: 8px 0 0; padding-left: 12px; border-left: 3px solid ${paper.border}; color: ${paper.secondaryText}; }
table { max-width: 100%; }
</style></head><body>${body}</body></html>`;
}

/**
 * @param {string} text
 */
function textToHtml(text) {
  const escaped = text.replace(/[&<>"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[character] ?? character);
  return escaped
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${paragraph.replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/**
 * Corpo do e-mail em iframe isolado: sem allow-scripts; allow-same-origin só para medir a altura.
 * @param {{ message: ConversationMessage }} props
 */
function EmailBodyFrame({ message }) {
  const iframeRef = useRef(/** @type {HTMLIFrameElement | null} */ (null));
  const [height, setHeight] = useState(IFRAME_MIN_HEIGHT);
  const colorScheme = colorSchemeState.value;
  const [documentHtml, setDocumentHtml] = useState('');

  // O esquema efetivo é aplicado no <html> por um effect; lê as cores depois dele.
  useEffect(() => {
    const frame = requestAnimationFrame(() => setDocumentHtml(buildEmailDocument(message.html || textToHtml(message.text ?? ''))));
    return () => cancelAnimationFrame(frame);
  }, [message.html, message.text, colorScheme]);

  useLayoutEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return undefined;
    /** @type {ResizeObserver | null} */
    let observer = null;
    const measure = () => {
      const contentDocument = iframe.contentDocument;
      if (!contentDocument?.body) return;
      setHeight(Math.max(IFRAME_MIN_HEIGHT, Math.ceil(contentDocument.documentElement.scrollHeight)));
    };
    const handleLoad = () => {
      measure();
      observer?.disconnect();
      const body = iframe.contentDocument?.body;
      if (body && 'ResizeObserver' in window) {
        observer = new ResizeObserver(measure);
        observer.observe(body);
      }
    };
    iframe.addEventListener('load', handleLoad);
    return () => {
      iframe.removeEventListener('load', handleLoad);
      observer?.disconnect();
    };
  }, []);

  return html`<iframe
    ref=${iframeRef}
    class="email-body-frame"
    title=${message.subject ?? t('conversations.email.noSubject')}
    sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
    referrerpolicy="no-referrer"
    srcdoc=${documentHtml}
    style=${{ height: `${height}px` }}
  ></iframe>`;
}

/**
 * @param {MessageParticipant} participant
 */
function formatParticipant(participant) {
  return participant.displayName && participant.displayName !== participant.handle ? `${participant.displayName} <${participant.handle}>` : participant.handle;
}

/**
 * @param {{ message: ConversationMessage, isExpanded: boolean, onToggle: () => void }} props
 */
function EmailMessageCard({ message, isExpanded, onToggle }) {
  const fromName = message.from?.displayName || message.from?.handle || '';
  const recipients = message.to ?? [];
  const toList = recipients.filter((participant) => (participant.role ?? 'TO') === 'TO');
  const ccList = recipients.filter((participant) => participant.role === 'CC');
  const attachments = message.attachments ?? [];
  const avatarSeed = message.direction === 'OUTGOING' ? message.sentBy?.workspaceMemberId ?? fromName : message.from?.handle;
  const avatarName = message.direction === 'OUTGOING' && message.sentBy ? message.sentBy.name : fromName;

  // conteúdo de conta conectada de um colega, restrito pela visibilidade (contrato 08 da agenda)
  if (/** @type {any} */ (message).isContentRestricted) {
    return html`<div class="email-card" data-expanded="false">
      <div class="email-card-header">
        <${Avatar} placeholder=${avatarName} seed=${avatarSeed} size="lg" type="rounded" />
        <span class="email-card-from"><span class="email-card-from-name">${fromName}</span></span>
        <span class="email-card-snippet"><${Icon} name="IconLock" size=${12} /> ${t('calendar.notShared')}</span>
        <time class="email-card-date" datetime=${message.sentAt} title=${formatFullDateTime(message.sentAt)}>${formatThreadListTime(message.sentAt)}</time>
      </div>
    </div>`;
  }

  if (!isExpanded) {
    return html`<button type="button" class="email-card" data-expanded="false" aria-expanded="false" aria-label=${t('conversations.email.expand')} onClick=${onToggle}>
      <div class="email-card-header">
        <${Avatar} placeholder=${avatarName} seed=${avatarSeed} size="lg" type="rounded" />
        <span class="email-card-from"><span class="email-card-from-name">${fromName}</span></span>
        <span class="email-card-snippet">${getMessageSnippet(message.text, message.html)}</span>
        ${attachments.length ? html`<${Icon} name="IconPaperclip" size=${14} className="email-card-clip" />` : null}
        <time class="email-card-date" datetime=${message.sentAt} title=${formatFullDateTime(message.sentAt)}>${formatThreadListTime(message.sentAt)}</time>
      </div>
    </button>`;
  }

  return html`<article class="email-card" data-expanded="true" data-status=${message.status}>
    <div class="email-card-header" role="button" tabindex="0" aria-expanded="true" aria-label=${t('conversations.email.collapse')} onClick=${onToggle}
      onKeyDown=${(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}>
      <${Avatar} placeholder=${avatarName} seed=${avatarSeed} size="lg" type="rounded" />
      <span class="email-card-from">
        <span class="email-card-from-name">${fromName}</span>
        <span class="email-card-from-address">${message.from?.handle ?? ''}</span>
      </span>
      <span class="email-card-header-right">
        <${MessageStatus} message=${message} />
        <time class="email-card-date" datetime=${message.sentAt} title=${formatFullDateTime(message.sentAt)}>${formatMediumDateTime(message.sentAt)}</time>
      </span>
    </div>
    <div class="email-card-recipients" title=${[...toList, ...ccList].map(formatParticipant).join(', ')}>
      ${toList.length ? html`<span><span class="email-card-recipients-label">${t('conversations.email.to')}:</span> ${toList.map((participant) => participant.displayName || participant.handle).join(', ')}</span>` : null}
      ${ccList.length ? html`<span><span class="email-card-recipients-label">${t('conversations.email.cc')}:</span> ${ccList.map((participant) => participant.displayName || participant.handle).join(', ')}</span>` : null}
    </div>
    <div class="email-card-body"><${EmailBodyFrame} message=${message} /></div>
    ${attachments.length
      ? html`<div class="email-card-attachments">
          ${attachments.map((attachment) =>
            attachment.kind === 'IMAGE'
              ? html`<div key=${attachment.fileId} class="email-card-image"><${ImageAttachment} attachment=${attachment} /></div>`
              : html`<${DocumentChip} key=${attachment.fileId} attachment=${attachment} />`,
          )}
        </div>`
      : null}
    ${message.status === 'FAILED'
      ? html`<div class="message-failed-note">${message.errorMessage ? t('conversations.failedWithReason', { reason: message.errorMessage }) : t('conversations.failedNoReason')}</div>`
      : null}
  </article>`;
}

/**
 * @param {{ thread: ConversationThread, onSend: (input: { text: string, subject: string, attachments: import('./message-composer.js').UploadedAttachment[] }) => void }} props
 */
function EmailReplyComposer({ thread, onSend }) {
  const [subject, setSubject] = useState(() => buildReplySubject(thread.subject));
  const [text, setText] = useState('');
  const fileInputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const pending = usePendingAttachments();
  const uploaded = pending.getUploaded();
  const canSend = !pending.isUploading && (text.trim() !== '' || uploaded.length > 0);
  // e-mail oculto pelo papel chega como null: mostra só o nome
  const recipient = thread.contact?.displayName && thread.contact.handle && thread.contact.displayName !== thread.contact.handle
    ? `${thread.contact.displayName} <${thread.contact.handle}>`
    : thread.contact?.handle ?? thread.contact?.displayName ?? '';

  const submit = () => {
    if (!canSend) return;
    onSend({ text: text.trim(), subject: subject.trim() || buildReplySubject(thread.subject), attachments: uploaded });
    setText('');
    pending.clear();
  };

  return html`<form class="email-reply" onSubmit=${(event) => {
    event.preventDefault();
    submit();
  }}>
    <div class="email-reply-header">
      <${Icon} name="IconCornerUpLeft" size=${16} />
      <span class="email-reply-title">${t('conversations.email.reply')}</span>
      <span class="email-reply-recipient" title=${recipient}>${t('conversations.email.to')}: ${recipient}</span>
    </div>
    <input class="email-reply-subject" value=${subject} aria-label=${t('conversations.email.subject')} placeholder=${t('conversations.email.subject')}
      onInput=${(event) => setSubject(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
    <textarea class="email-reply-body" rows="4" value=${text} placeholder=${t('conversations.email.bodyPlaceholder')} aria-label=${t('conversations.email.bodyPlaceholder')}
      onInput=${(event) => setText(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)}
      onKeyDown=${(event) => {
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          submit();
        }
      }}></textarea>
    <${PendingAttachmentList} attachments=${pending.attachments} onRemove=${pending.removeAttachment} />
    <div class="email-reply-footer">
      <${LightIconButton} icon="IconPaperclip" size="medium" accent="tertiary" ariaLabel=${t('conversations.composer.attach')} onClick=${() => fileInputRef.current?.click()} />
      <input ref=${fileInputRef} type="file" multiple hidden onChange=${(event) => {
        const input = /** @type {HTMLInputElement} */ (event.currentTarget);
        pending.addFiles(input.files);
        input.value = '';
      }} />
      <${Button} title=${t('conversations.email.send')} icon="IconSend" accent="blue" size="small" type="submit" disabled=${!canSend} hotkeyLabel="⌘⏎" />
    </div>
  </form>`;
}

/**
 * @param {{ thread: ConversationThread, messages: ConversationMessage[], hasOlder: boolean, isLoadingOlder: boolean, onLoadOlder: () => void,
 *   onSendReply: (input: { text: string, subject: string, attachments: import('./message-composer.js').UploadedAttachment[] }) => void }} props
 */
export function EmailThread({ thread, messages, hasOlder, isLoadingOlder, onLoadOlder, onSendReply }) {
  const [expandedIds, setExpandedIds] = useState(/** @type {Set<string>} */ (new Set()));
  const [isMiddleExpanded, setIsMiddleExpanded] = useState(false);
  const scrollRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const lastMessage = messages[messages.length - 1];
  const lastMessageId = lastMessage?.id;

  // Ao abrir o fio, ou quando chega uma mensagem nova, mostra a última no fim da tela.
  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [lastMessageId]);

  /** @param {string} messageId */
  const toggle = (messageId) =>
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(messageId)) next.delete(messageId);
      else next.add(messageId);
      return next;
    });
  /** @param {ConversationMessage} message */
  const isExpanded = (message) => (message.id === lastMessageId ? !expandedIds.has(message.id) : expandedIds.has(message.id));

  const shouldCollapseMiddle = messages.length >= COLLAPSE_MIDDLE_THRESHOLD && !isMiddleExpanded;
  const middleCount = messages.length - 3;
  const visibleMessages = shouldCollapseMiddle ? [messages[0], messages[1], null, lastMessage] : messages;

  return html`<div class="email-thread" ref=${scrollRef}>
    <div class="email-thread-inner">
      ${hasOlder
        ? html`<div class="messages-load-older"><${LightButton} title=${t('conversations.loadOlder')} icon="IconChevronUp" disabled=${isLoadingOlder} onClick=${onLoadOlder} /></div>`
        : null}
      <div class="email-thread-cards">
        ${visibleMessages.map((message) =>
          message === null
            ? html`<button key="middle" type="button" class="email-thread-middle" onClick=${() => setIsMiddleExpanded(true)}>
                <${Icon} name="IconArrowsVertical" size=${14} />
                <span>${t('conversations.email.olderMessages', { count: middleCount })}</span>
              </button>`
            : html`<${EmailMessageCard} key=${message.id} message=${message} isExpanded=${isExpanded(message)} onToggle=${() => toggle(message.id)} />`,
        )}
      </div>
      <${EmailReplyComposer} key=${thread.id} thread=${thread} onSend=${onSendReply} />
    </div>
  </div>`;
}
